import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Save } from 'lucide-react';

import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { DetailHeader } from '@/ui/DetailHeader';
import { ErrorState } from '@/ui/ErrorState';
import { GlossaryTerm } from '@/ui/GlossaryTerm';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';

import { useAtualizarMovimento, useMovimentosDre } from './hooks/useApuracao';
import { useDres } from './hooks/useDres';
import type { MovimentoApuracaoLinha } from './types';

/** Ms de inatividade antes de mandar o delta para a API (autosave sem botão). */
const ATRASO_AUTOSAVE_MS = 800;

type CampoEditavel =
  'diasOperados' | 'kmRodados' | 'diasAtivComplementarPresencial' | 'ajusteValor';

type LinhaGrade = MovimentoApuracaoLinha & { id: string };

function formatarMesLabel(mes: string): string {
  const [ano, mesNum] = mes.split('-').map(Number);
  if (!ano || !mesNum) return mes;
  const texto = new Date(ano, mesNum - 1, 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/**
 * FIN-061: grade de apuração por OS de uma DRE, num mês.
 *
 * Edição em linha, sem botão de salvar por célula: o campo muda o estado
 * local na hora (Optimistic UI) e, depois de ~800ms sem nova tecla, manda só
 * o delta (PATCH) para a API — nunca a linha inteira. "Salvar Rascunho" força
 * o envio imediato de tudo que ainda está pendente; "Marcar Concluído" grava
 * o status final de cada OS.
 */
export function ApuracaoGridPage() {
  const { mes = '', dreId = '' } = useParams<{ mes: string; dreId: string }>();
  const navegar = useNavigate();
  const { data, isLoading, isError, refetch } = useMovimentosDre(mes, dreId);
  const { data: dres } = useDres();
  const atualizar = useAtualizarMovimento(mes, dreId);

  const dre = dres?.find((item) => item.id === dreId);
  const dreLabel = dre ? `${dre.codigo} — ${dre.nome}` : 'DRE';
  const mesLabel = mes ? formatarMesLabel(mes) : '';

  const [edicoes, definirEdicoes] = useState<
    Record<string, Partial<Record<CampoEditavel, number>>>
  >({});
  const [sujas, definirSujas] = useState<Set<string>>(new Set());
  const [concluido, definirConcluido] = useState(false);
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const linhas: LinhaGrade[] = useMemo(
    () =>
      (data ?? []).map((linha) => {
        const edicao = edicoes[linha.ordemServicoId];
        const diasOperados = edicao?.diasOperados ?? linha.diasOperados;
        return {
          ...linha,
          id: linha.ordemServicoId,
          diasOperados,
          diasFalta: Math.max(0, linha.diasUteisMes - diasOperados),
          kmRodados: edicao?.kmRodados?.toFixed(2) ?? linha.kmRodados,
          diasAtivComplementarPresencial:
            edicao?.diasAtivComplementarPresencial ?? linha.diasAtivComplementarPresencial,
          ajusteValor: edicao?.ajusteValor?.toFixed(2) ?? linha.ajusteValor,
        };
      }),
    [data, edicoes],
  );

  const totalLinhas = linhas.length;
  const concluidas = linhas.filter((l) => l.status === 'CONCLUIDO').length;
  const pctConcluido = totalLinhas > 0 ? Math.round((concluidas / totalLinhas) * 100) : 0;
  const diasLetivos = linhas[0]?.diasUteisMes;

  function editarCampo(ordemServicoId: string, campo: CampoEditavel, valor: number) {
    definirEdicoes((atual) => ({
      ...atual,
      [ordemServicoId]: { ...atual[ordemServicoId], [campo]: valor },
    }));
    definirSujas((atual) => new Set(atual).add(ordemServicoId));

    const chave = `${ordemServicoId}:${campo}`;
    clearTimeout(timers.current[chave]);
    timers.current[chave] = setTimeout(() => {
      atualizar.mutate(
        { ordemServicoId, dados: { [campo]: valor } },
        {
          onSuccess: () =>
            definirSujas((atual) => {
              const proximo = new Set(atual);
              proximo.delete(ordemServicoId);
              return proximo;
            }),
        },
      );
    }, ATRASO_AUTOSAVE_MS);
  }

  async function salvarRascunho() {
    if (sujas.size === 0) return;
    await Promise.all(
      [...sujas].map((ordemServicoId) => {
        const edicao = edicoes[ordemServicoId] ?? {};
        return atualizar.mutateAsync({ ordemServicoId, dados: edicao });
      }),
    );
    definirSujas(new Set());
  }

  async function marcarConcluido() {
    await salvarRascunho();
    await Promise.all(
      linhas.map((l) =>
        atualizar.mutateAsync({ ordemServicoId: l.ordemServicoId, dados: { status: 'CONCLUIDO' } }),
      ),
    );
    definirConcluido(true);
  }

  function campoNumerico(linha: LinhaGrade, campo: CampoEditavel, step = 1) {
    return (
      <Input
        type="number"
        inputMode="decimal"
        step={step}
        className="mx-auto h-8 w-20 text-center"
        value={linha[campo] as string | number}
        min={0}
        aria-label={campo}
        onChange={(evento) => {
          const valor = Number(evento.target.value);
          if (!Number.isNaN(valor)) editarCampo(linha.ordemServicoId, campo, valor);
        }}
      />
    );
  }

  const colunas: DataTableColumn<LinhaGrade>[] = [
    {
      header: 'Nº OS',
      sortKey: 'numeroOs',
      cell: (linha) => (
        <Link
          to={`/operacao/financeiro/apuracao/lancamento/${linha.ordemServicoId}/${mes}`}
          className="font-medium text-primary underline-offset-2 hover:underline"
        >
          #{linha.numeroOs}
        </Link>
      ),
    },
    {
      header: 'Credenciado',
      accessorKey: 'credenciadoNome',
      sortKey: 'credenciadoNome',
      cellClassName: 'max-w-[160px] truncate',
    },
    { header: 'Tipo', cell: (linha) => <Badge variant="outline">{linha.modalidade}</Badge> },
    {
      header: 'Km Rota',
      accessorKey: 'kmRotaBase',
      cellClassName: 'text-center',
      headerClassName: 'text-center',
    },
    {
      header: 'Dias Op.',
      headerTermo: 'DIAS_OPERADOS',
      cellClassName: 'text-center',
      headerClassName: 'text-center',
      cell: (linha) => campoNumerico(linha, 'diasOperados'),
    },
    {
      header: 'Faltas',
      cellClassName: 'text-center',
      headerClassName: 'text-center',
      cell: (linha) => (
        <span className={linha.diasFalta > 0 ? 'font-medium text-destructive' : ''}>
          {linha.diasFalta}
        </span>
      ),
    },
    {
      header: 'Km Rodado',
      cellClassName: 'text-center',
      headerClassName: 'text-center',
      cell: (linha) => campoNumerico(linha, 'kmRodados', 0.1),
    },
    {
      header: 'Ativ. Comp.',
      headerTermo: 'ATIV_COMPLEMENTAR',
      cellClassName: 'text-center',
      headerClassName: 'text-center',
      cell: (linha) => campoNumerico(linha, 'diasAtivComplementarPresencial'),
    },
    {
      header: 'Ajuste (R$)',
      headerTermo: 'AJUSTE_VALOR',
      cellClassName: 'text-center',
      headerClassName: 'text-center',
      cell: (linha) => campoNumerico(linha, 'ajusteValor', 0.01),
    },
    {
      header: 'Status',
      cellClassName: 'text-center',
      headerClassName: 'text-center',
      cell: (linha) =>
        sujas.has(linha.ordemServicoId) ? (
          <span
            className="inline-block h-2 w-2 rounded-full bg-warning"
            title="Modificado (não salvo)"
          />
        ) : linha.status === 'CONCLUIDO' ? (
          <Badge variant="default">Concluído</Badge>
        ) : linha.status === 'RASCUNHO' ? (
          <Badge variant="secondary">Rascunho</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">Novo</span>
        ),
    },
  ];

  if (isError) {
    return (
      <ErrorState
        variant="load-failed"
        primaryAction={{ label: 'Tentar novamente', onClick: () => void refetch() }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <DetailHeader
        backTo={`/operacao/financeiro/apuracao-mensal?mes=${mes}`}
        backLabel="Apuração"
        title={`Apuração — ${dreLabel}`}
        subtitle={
          <span>
            Competência <strong>{mesLabel}</strong> · Dias letivos: {diasLetivos ?? 'N/D'} ·{' '}
            <span className={pctConcluido === 100 ? 'font-medium text-success' : 'font-medium'}>
              {concluidas}/{totalLinhas} concluídos ({pctConcluido}%)
            </span>
            {sujas.size > 0 && (
              <>
                {' · '}
                <span className="font-medium text-warning">{sujas.size} não salvos</span>
              </>
            )}
          </span>
        }
      />

      {concluido && (
        <div className="flex flex-col gap-3 rounded-lg border border-success/40 bg-success/5 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm">
            <p className="font-medium text-foreground">Apuração concluída para esta DRE.</p>
            <p className="text-muted-foreground">
              Próximo passo do ciclo: realizar o{' '}
              <GlossaryTerm termo="SELAMENTO">selamento mensal</GlossaryTerm> da competência ou
              avançar para a <GlossaryTerm termo="MEDICAO">medição</GlossaryTerm>.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navegar(`/operacao/financeiro/apuracao-mensal?mes=${mes}`)}
            >
              Voltar à apuração
            </Button>
            <Button size="sm" onClick={() => navegar('/operacao/financeiro/selamento-mensal')}>
              Ir para selamento <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      )}

      <div
        className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground"
        role="region"
        aria-label="Legenda de status do grid"
      >
        <span className="font-medium text-foreground">Legenda:</span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-warning" aria-hidden="true" />{' '}
          Modificado (não salvo)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-2.5 rounded-full bg-muted-foreground/40"
            aria-hidden="true"
          />{' '}
          Rascunho
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-success" aria-hidden="true" />{' '}
          Concluído
        </span>
        <span>
          <span className="font-medium text-destructive">Faltas</span> em vermelho indicam dias não
          operados
        </span>
      </div>

      <DataTable
        columns={colunas}
        data={linhas}
        isLoading={isLoading}
        emptyTitle="Nenhuma ordem de serviço ativa"
        emptyDescription="Não há OS em execução para esta DRE nesta competência."
        ariaLabel="Grade de apuração"
      />

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => void salvarRascunho()}
          disabled={sujas.size === 0 || atualizar.isPending}
        >
          <Save className="mr-1 h-4 w-4" aria-hidden="true" /> Salvar Rascunho
        </Button>
        <Button
          onClick={() => void marcarConcluido()}
          disabled={totalLinhas === 0 || atualizar.isPending}
        >
          <CheckCircle2 className="mr-1 h-4 w-4" aria-hidden="true" /> Marcar Concluído
        </Button>
      </div>
    </div>
  );
}

export default ApuracaoGridPage;
